import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Row, Select, Space, Table, Typography, message, Modal as AntModal, Spin } from "antd";
import type { TableProps } from "antd";
import { usePagination } from "src/hook/usePagination";
import { useDidMount } from "src/hook/useDidMount";
import { useShallow } from "zustand/react/shallow";
import { profileStore } from "src/stores/profile-store";
import { utilsStore } from "src/stores/utils-store";
import "./ReportWWVocEmission.css";
import { InfoCircleOutlined, CloseOutlined } from "@ant-design/icons";

import { excelStore } from "src/stores/excel-store.interface";
import { EmissionObject, EmissionObjectEnum, MonthsDisplayNoTotal, UnitAllEnum } from "src/entity/common/unit.constant";
import { TbRefresh } from "react-icons/tb";
import ModalApproveMonth from "src/components/ModalApproveMonth/ModalApproveMonth";
import { componentPalette } from "src/theme/component-palette";
import { BsTable } from "react-icons/bs";
import { HiOutlineClipboardDocumentCheck } from "react-icons/hi2";
import { PiChartBarFill } from "react-icons/pi";
import { RolesNameEnum } from "src/entity/common/roles.enum";
import ModalApproveStep from "src/components/ModalApproveStep/ModalApproveStep";
import { useSearchParams } from "react-router-dom";
import { ApproveBody, ResponseAPI } from "src/entity/voc-approve/voc-approve.entity";
import { CommonError, CommonSuccess } from "src/entity/common/menus-enum.enum";
import useNavigationMenu from "src/hook/useNavigationMenu";
import {
  canApproveAndViewMainReport,
  canApproveAndViewTable,
} from "src/pages/VocsInventory/WasteWaterTreatment/PermissionCheck/PermissionCheck";
import { getFilterWithEmptyMasterdata } from "src/utils/utils-object";
import { setupWasteWaterStore } from "src/stores/waste-water-info-store";
import { reportWasteWaterStore } from "src/stores/waste-water-report";
import {
  FilterReportVocEmission,
  ObjectSummaryWithUnit,
  ReportMonthData,
  ReportTotalMonthData,
  ReportUnitValue,
  ResponseReportVocEmission,
  TmpReportEmissionData,
  VocEmissionReport,
} from "src/entity/waste-water/report-voc-emission.entity";
import { wasteWaterApproveStore } from "src/stores/waste-water-approve-store";
import { formatNumber, parseAndFormatScientificNumber, parseToFixedDecimalWithComma } from "src/utils/process-number";
import { convertEmissionWord } from "src/entity/common/global.entity";
import { optionsSelectYears } from "src/utils/array";
import { addCenterAlign } from "src/components/AntdCustomPart/TableTitle.tsx/TableTitle";

const { Text } = Typography;
interface ReportWWVocEmissionTableProps {}

const ReportWWVocEmissionTable: React.FC<ReportWWVocEmissionTableProps> = ({}) => {
  const menu = "wasteWater";
  const currentYear = new Date().getFullYear();
  const [messageApi, contextHolder] = message.useMessage();

  const [searchParams, setSearchParams] = useSearchParams();
  const approveId = searchParams.get("approveId");
  const yearParam = searchParams.get("year");
  const monthParam = searchParams.get("month");
  const approveNoParam = searchParams.get("approveNo");

  const prevCompany: any = useRef();

  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [selectedUnit, setSelectedUnit] = useState<UnitAllEnum>(UnitAllEnum.KG);
  const [openModalApproveMonth, setOpenModalApproveMonth] = useState<boolean>(false);
  const [selectedButton, setSelectedButton] = useState<string>("table");
  const [openModalApproveStep, setOpenModalApproveStep] = useState<boolean>(false);
  const [tableKey, setTableKey] = useState(0);
  const [selectedEmission, setSelectedEmission] = useState<string>("tvoc");
  const { permissions } = useNavigationMenu();

  // 1. Profile zone !
  const profile = profileStore(useShallow((state) => state.profile));
  const loadingProfile = profileStore(useShallow((state) => state.loading));
  const userId = profile?.userId;
  const email = profile?.email;
  const name = profile?.name;

  // 2. Master data zone
  const setLoadingAll = reportWasteWaterStore(useShallow((state) => state.setLoadingAll));
  const loadingAll = reportWasteWaterStore(useShallow((state) => state.loadingAll));
  const getAreaInCompany = utilsStore(useShallow((state) => state.getAreaInCompany));
  const dataAreaInCompany = utilsStore(useShallow((state) => state.dataAreaInCompany));
  const getMasterDataEmission = utilsStore(useShallow((state) => state.getMasterDataEmission));
  const masterDataEmission = utilsStore(useShallow((state) => state.masterDataEmission));
  const getAllSettingWasteWater = setupWasteWaterStore(useShallow((state) => state.getAllSettingWasteWater));

  // 3. Report Voc Zone
  const searchReportVocEmission = reportWasteWaterStore(useShallow((state) => state.searchReportVocEmission));
  const dataReportVoc = reportWasteWaterStore(useShallow((state) => state.dataReportVoc));
  const getFilterColumnReportVocEmission = reportWasteWaterStore(useShallow((state) => state.getFilterColumnReportVocEmission));
  const filterReportVocEmission = reportWasteWaterStore(useShallow((state) => state.filterReportVocEmission));
  const calculateReportAfterRejectApproval = reportWasteWaterStore(useShallow((state) => state.calculateReportVocAfterRejectApproval));

  const selectedYearExcel = excelStore(useShallow((state) => state.selectedYear));
  const setSelectedYearExcel = excelStore(useShallow((state) => state.setSelectedYear));
  const queryStr = excelStore(useShallow((state) => state.queryStr));
  const setQueryStringExcel = excelStore(useShallow((state) => state.setQueryStr));
  // console.log("[Waste water] report : ", { queryStr, selectedYearExcel });

  const getMonthCanApprove = wasteWaterApproveStore(useShallow((state) => state.getMonthCanApprove));
  const loadingOptionMonth = wasteWaterApproveStore(useShallow((state) => state.loading));
  const optionMonth = wasteWaterApproveStore(useShallow((state) => state.optionMonth));
  const createSubmitReportVoc = wasteWaterApproveStore(useShallow((state) => state.createSubmitReportVoc));
  const loadingSubmitVocReport = wasteWaterApproveStore(useShallow((state) => state.loadingSubmitVocReport));
  const getInfoStepApprove = wasteWaterApproveStore(useShallow((state) => state.getInfoStepApprove));
  const loadingGetApprove = wasteWaterApproveStore(useShallow((state) => state.loadingGetApprove));
  const approveData = wasteWaterApproveStore(useShallow((state) => state.approveData));
  const selectedApprove = wasteWaterApproveStore(useShallow((state) => state.selectedApprove));
  const getOptionApprove = wasteWaterApproveStore(useShallow((state) => state.getOptionApprove));
  const optionApprove = wasteWaterApproveStore(useShallow((state) => state.optionApprove));
  const updateApproveStep = wasteWaterApproveStore(useShallow((state) => state.updateApproveStep));

  // For new ui approval page
  const [selectedYearApprove, setSelectedYearApprove] = useState<number | null>();
  const [selectedMonthApprove, setSelectedMonthApprove] = useState<string | null>();
  const [selectedNoApprove, setSelectedNoApprove] = useState<number | null>();

  const fetchInfoStepApprove = async () => {
    if (selectedYearApprove && selectedMonthApprove && selectedNoApprove) {
      const query = new URLSearchParams({
        menu,
        year: selectedYearApprove.toString(),
        month: selectedMonthApprove,
        approveNo: selectedNoApprove.toString(),
      }).toString();
      getInfoStepApprove(query);
    } else {
      getInfoStepApprove("");
    }
  };
  const fetchMonthOfSubmit = async () => {
    await getMonthCanApprove(selectedYear);
  };

  const removeSarchParam = () => {
    searchParams.delete("year");
    searchParams.delete("month");
    searchParams.delete("approveNo");
    searchParams.delete("companyId");
  };

  const resetSelections = () => {
    if (yearParam && monthParam && approveNoParam) {
      handleButtonMenuClick("approve");
      if (yearParam) setSelectedYearApprove(Number(yearParam));
      if (monthParam) setSelectedMonthApprove(monthParam);
      if (approveNoParam) setSelectedNoApprove(Number(approveNoParam));
      removeSarchParam();
    } else {
      setSelectedYearApprove(null);
      setSelectedMonthApprove(null);
      setSelectedNoApprove(null);
    }
  };

  const fetchOptionVocApprove = async () => {
    if (yearParam && !selectedYearApprove && !selectedMonthApprove) return;

    const queryParams = new URLSearchParams({ menu });
    if (selectedYearApprove) {
      queryParams.append("year", selectedYearApprove.toString());
    }
    if (selectedMonthApprove) {
      queryParams.append("month", selectedMonthApprove);
    }

    const queryString = queryParams.toString();

    const approves = await getOptionApprove(queryString);
    // console.log("ApproveOption HERE : ", approves);
  };

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    setLimit: setPageSize,
    data: sourceData,
    replaceFilter,
  } = usePagination<ResponseReportVocEmission, FilterReportVocEmission>({
    action: searchReportVocEmission,
    remoteData: dataReportVoc.data,
    initialFilter: { year: [selectedYear], unit: selectedUnit, emission: selectedEmission },
  });

  // console.log("Source data :: ", dataReportVoc);

  const handleButtonMenuClick = (buttonName: string) => {
    // console.log("on click : ", buttonName, approveId);
    setSelectedButton(buttonName);
    if (buttonName === "table") {
      setOpenModalApproveStep(false);
      if (approveId) {
        searchParams.delete("approveId");
        searchParams.delete("companyId");
        setSearchParams(searchParams);
      }
    } else if (buttonName === "approve") {
      fetchOptionVocApprove();
      setOpenModalApproveStep(true);
    }
  };

  useEffect(() => {
    if (approveId) {
      handleButtonMenuClick("approve");
    }
  }, [approveId]);

  const getMasterLabel = useCallback(
    (type: string, text: string) => {
      if (!text || typeof text != "string") return "";

      if (type === "area" && dataAreaInCompany) {
        const optValue = dataAreaInCompany.find((item) => item._id === text);
        return optValue?.areaName;
      } else return text;
    },
    [dataReportVoc, dataAreaInCompany],
  );

  const wasteWaterUnitFilter = useMemo(() => {
    return filterReportVocEmission.wasteWaterUnit.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [dataReportVoc, filterReportVocEmission.wasteWaterUnit]);
  const areaFilter = useMemo(() => {
    return getFilterWithEmptyMasterdata(dataAreaInCompany, filterReportVocEmission.area, "areaName");
  }, [dataAreaInCompany, filterReportVocEmission.area]);

  const handleTableChange: TableProps<ResponseReportVocEmission>["onChange"] = (_, filters) => {
    const mergeFilter = { ...filters, unit: selectedUnit };
    setQueryStringExcel(JSON.stringify(mergeFilter));
    onChange(mergeFilter);
  };
  const handleChangeUnit = () => {
    if (selectedUnit === UnitAllEnum.KG) {
      setSelectedUnit(UnitAllEnum.TON);
      const tmpQuery = queryStr ? { ...JSON.parse(queryStr), unit: UnitAllEnum.TON } : { unit: UnitAllEnum.TON };
      setQueryStringExcel(JSON.stringify(tmpQuery));
    } else if (selectedUnit === UnitAllEnum.TON) {
      const tmpQuery = queryStr ? { ...JSON.parse(queryStr), unit: UnitAllEnum.KG } : { unit: UnitAllEnum.KG };
      setQueryStringExcel(JSON.stringify(tmpQuery));
      setSelectedUnit(UnitAllEnum.KG);
    }
  };

  // console.log("dataReportVoc.data :: ", { reportVOC: dataReportVoc.data, filter });
  const addWidthWhenEmptyData = useCallback(
    (width: string, isForce?: string) => {
      if (dataReportVoc.data.length === 0) return { width };
      else if (isForce) return { width: isForce };
      else return {};
    },
    [dataReportVoc.data],
  );

  const getValueFromObjectMonth = (objectMonth: ReportMonthData) => {
    if (objectMonth.freeze[`value${selectedUnit}` as keyof ReportUnitValue] != null)
      return parseAndFormatScientificNumber(`${objectMonth.freeze[`value${selectedUnit}` as keyof ReportUnitValue]}`, 2);
    else if (objectMonth[`value${selectedUnit}` as keyof ReportUnitValue] != null)
      return parseAndFormatScientificNumber(`${objectMonth[`value${selectedUnit}` as keyof ReportUnitValue]}`, 2);
  };

  const monthColumns: TableProps<ResponseReportVocEmission>["columns"] = MonthsDisplayNoTotal.map((item) => {
    return {
      title: item.title,
      key: item.dataIndex,
      render: (_, record) => {
        const showValue = getValueFromObjectMonth(
          record.vocEmission[selectedEmission as keyof VocEmissionReport][item.dataIndex as keyof TmpReportEmissionData],
        );
        return <Text className="pr-3">{showValue ?? 0}</Text>;
      },
      align: "left",
      //   ...addWidthWhenEmptyData("3.5rem"),
    };
  });

  const getTotalValueFromObjectMonth = (objectMonth: ReportTotalMonthData) => {
    if (objectMonth.freeze[`value${selectedUnit}` as keyof ReportUnitValue] != null)
      return parseAndFormatScientificNumber(`${objectMonth.freeze[`value${selectedUnit}` as keyof ReportUnitValue]}`, 2);
    else if (objectMonth[`value${selectedUnit}` as keyof ReportUnitValue] != null)
      return parseAndFormatScientificNumber(`${objectMonth[`value${selectedUnit}` as keyof ReportUnitValue]}`, 2);
  };

  const getIndex = (index: number) => (page - 1) * pageSize + index;
  const columns: TableProps<ResponseReportVocEmission>["columns"] = [
    {
      title: "No",
      key: "no",
      render: (_, __, index) => getIndex(index + 1),
    },
    {
      title: "Wastewater Unit",
      key: "wasteWaterUnit",
      render: (_, record) => <Text>{record.wasteWaterUnit}</Text>,
      filters: wasteWaterUnitFilter,
      ...addWidthWhenEmptyData("10rem", "12rem"),
    },
    {
      title: "Area",
      key: "area",
      render: (_, record) => <Text>{getMasterLabel("area", record.area)}</Text>,
      filters: areaFilter,
      ...addWidthWhenEmptyData("8rem", "8rem"),
    },
    {
      title: "Emission",
      key: "emission",
      render: (_) => <Text>{convertEmissionWord(selectedEmission.toUpperCase())}</Text>,
      ...addWidthWhenEmptyData("8rem", "8rem"),
    },
    {
      title: (
        <>
          <Space>
            Unit
            <Button onClick={handleChangeUnit} type="text" icon={<TbRefresh />} />
          </Space>
        </>
      ),
      key: "unit",
      render: (_, __) => <Text>{selectedUnit}</Text>,
      width: 80,
    },
    ...monthColumns,
    {
      title: "Total",
      render: (_, record) => (
        <Text>{getTotalValueFromObjectMonth(record.vocEmission[selectedEmission as keyof VocEmissionReport].totalValue)}</Text>
      ),
      ...addWidthWhenEmptyData("4rem"),
    },
  ];

  // Note : get option in dorpdonw tvoc, bd, bz ...

  const initialRequestDidMount = async (isDidMount?: boolean) => {
    setLoadingAll(true);
    const getEmissionOptions = getMasterDataEmission();
    const getAreaInCompanyPromise = getAreaInCompany(profile?.activeCompany._id as string); // area
    const getAllSettingWasteWaterPromise = getAllSettingWasteWater(); // flare name
    const getFilterColumnReportVocEmissionPromise = getFilterColumnReportVocEmission(selectedYear);
    const searchReportVocEmissionPromise = searchReportVocEmission(
      isDidMount === true
        ? { ...filter, page, limit: pageSize, year: [selectedYear], unit: selectedUnit, emission: selectedEmission.toLowerCase() }
        : { page, limit: pageSize, year: [selectedYear], unit: selectedUnit, emission: selectedEmission.toLowerCase() },
    );

    await Promise.all([
      getEmissionOptions,
      getAreaInCompanyPromise,
      getAllSettingWasteWaterPromise,
      getFilterColumnReportVocEmissionPromise,
      searchReportVocEmissionPromise,
    ]);
    setLoadingAll(false);
  };

  useDidMount(() => {
    prevCompany.current = profile?.activeCompany._id;
    initialRequestDidMount(true);

    setQueryStringExcel(JSON.stringify({ year: [selectedYear], unit: selectedUnit, emission: selectedEmission }));
  });

  // ** NOTE : Trigger for Request new data -> when change company, year
  useEffect(() => {
    if (profile?.activeCompany) {
      if (prevCompany.current !== profile.activeCompany._id) {
        replaceFilter({ year: [currentYear.toString()], unit: selectedUnit, emission: selectedEmission });
        setSelectedYear(currentYear.toString());
        setSelectedYearExcel(currentYear.toString());
        setQueryStringExcel(JSON.stringify({ year: [currentYear.toString()], unit: selectedUnit, emission: selectedEmission }));
        resetSelections();
        fetchOptionVocApprove();
        prevCompany.current = profile?.activeCompany._id;
      }
      setTableKey(tableKey + 1);
    }
  }, [profile?.activeCompany, selectedYear, selectedEmission]);

  useEffect(() => {
    if (yearParam) {
      setSelectedYear(yearParam);
      searchParams.delete("year");
    }
  }, [yearParam]);

  useEffect(() => {
    if (tableKey > 0) {
      initialRequestDidMount(false);
      fetchOptionVocApprove();
      fetchInfoStepApprove();
    }
  }, [tableKey]);

  useEffect(() => {
    if (optionApprove && "approveNo" in optionApprove) {
      const { approveNo } = optionApprove || {};
      if (approveNo) {
        if (!selectedNoApprove && selectedMonthApprove && approveNo?.length === 1) {
          setSelectedNoApprove(approveNo[0]);
        }
      } else {
        resetSelections();
      }
    }
  }, [optionApprove]);

  useEffect(() => {
    if (yearParam && approveNoParam) {
      handleButtonMenuClick("approve");
      if (yearParam) setSelectedYearApprove(Number(yearParam));
      if (monthParam) setSelectedMonthApprove(monthParam);
      if (approveNoParam) setSelectedNoApprove(Number(approveNoParam));
    }
  }, [yearParam, monthParam, approveNoParam]);

  useEffect(() => {
    fetchOptionVocApprove();
    fetchInfoStepApprove();
  }, [selectedYearApprove, selectedMonthApprove, selectedNoApprove]);

  const handleChangeYear = (year: string) => {
    // console.log("Year : ", year);
    replaceFilter({ year: [year], unit: selectedUnit, emission: selectedEmission });
    setSelectedYearExcel(year);
    setQueryStringExcel(JSON.stringify({ year: [year], unit: selectedUnit, emission: selectedEmission }));
    setSelectedYear(year);
  };
  const buttonStyle = (buttonName: string) => ({
    backgroundColor: selectedButton === buttonName ? componentPalette.primary : "initial",
    color: selectedButton === buttonName ? "white" : "initial",
  });
  const handleOpenModalApproveMonth = () => {
    fetchMonthOfSubmit();
    setOpenModalApproveMonth(true);
  };

  const warningAlert = (message: string | string[], action: string) => {
    const warnModal = AntModal.confirm({
      title: (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Can not {action}</span>
          <CloseOutlined onClick={() => warnModal.destroy()} style={{ cursor: "pointer" }} />
        </div>
      ),
      content: message,
      okText: "OK",
      icon: <InfoCircleOutlined style={{ color: componentPalette.redThird }} />,
      okButtonProps: { style: { textAlign: "center", backgroundColor: componentPalette.primary } },
      cancelButtonProps: { style: { display: "none" } },
      onOk: () => {},
      className: "custom-tmp-modal-warning",
    });
  };

  const handleSubmitReport = async (checkMonths: any) => {
    const userId = profile?.userId;
    const payload = {
      userId: userId as string,
      months: checkMonths,
      year: selectedYear,
    };
    createSubmitReportVoc(payload)
      .then(() => {
        setOpenModalApproveMonth(false);
        messageApi.open({
          type: "success",
          content: CommonSuccess.SUBMIT_SUCCESSFULL,
        });
      })
      .catch((error) => {
        const responseData = error?.response?.data as ResponseAPI;
        warningAlert(String(responseData.message), "submit");
        return;
        return;
      });
  };

  const handleApproveReport = async (approveBody: ApproveBody, action: string) => {
    updateApproveStep(approveBody)
      .then(() => {
        if (action === "approved") {
          messageApi.open({
            type: "success",
            content: CommonSuccess.APPROVED_SUCCESSFULL,
          });
        } else if (action === "reject") {
          messageApi.open({
            type: "success",
            content: CommonSuccess.REJECT_SUCCESSFULL,
          });
          // const findSlot = optionApprove.find((item) => item.value == approveBody.approveId);
          // const extractYear = findSlot?.label.split(" / ") as string[];

          // calculateReportAfterRejectApproval(extractYear[1]);
          calculateReportAfterRejectApproval(String(selectedYearApprove));
        }
        fetchInfoStepApprove();
        fetchMonthOfSubmit();
      })
      .catch((error) => {
        const actionStr = action === "approved" ? "approve" : "reject";
        warningAlert(error?.response?.data?.message || error?.message, actionStr);
        return;
      });
  };

  const isDisableSubmitButton = () => {
    // console.log("permissions : ", { dataLength: dataReportVoc.data?.length, optionMonthLength: optionMonth, permissions });
    if (!canApproveAndViewTable(permissions) || dataReportVoc.data?.length == 0) {
      return true;
    } else {
      return false;
    }
  };

  const handleChangeEmission = (value: string) => {
    setSelectedEmission(value);

    onChange({ emission: value });
    const tmpQuery = queryStr ? { ...JSON.parse(queryStr), emission: value } : { emission: value };
    setQueryStringExcel(JSON.stringify(tmpQuery));
  };

  // console.log("Emission selected : ", selectedEmission);

  const handleYearApproveChange = (value: number) => {
    setSelectedYearApprove(value);
    setSelectedMonthApprove(null);
    setSelectedNoApprove(null);
  };

  const handleMonthApprovalChange = (value: string) => {
    setSelectedMonthApprove(value);
    setSelectedNoApprove(null);
  };

  const handleApproveNoChange = (value: number) => {
    setSelectedNoApprove(value);
  };

  return (
    <Row className="w-full" id={"wrap-report-waste-water-voc-table"}>
      {contextHolder}
      <Space direction={"horizontal"} align={"start"}>
        {selectedButton === "table" ? (
          <Select value={selectedYear} style={{ width: 120 }} onChange={handleChangeYear} options={optionsSelectYears()} size="middle" />
        ) : (
          <Space>
            <Select
              value={selectedYearApprove}
              style={{ width: 120 }}
              onChange={handleYearApproveChange}
              options={optionApprove?.year?.map((year) => ({ label: year, value: year }))}
              loading={loading}
              size="middle"
            />
            <Select
              value={selectedMonthApprove}
              style={{ width: 120 }}
              onChange={handleMonthApprovalChange}
              options={optionApprove?.month?.map((month) => ({ label: month.charAt(0).toUpperCase() + month.slice(1), value: month }))}
              loading={loading}
              size="middle"
            />
            <Select
              value={selectedNoApprove}
              style={{ width: 120 }}
              onChange={handleApproveNoChange}
              options={optionApprove?.approveNo?.map((approveNo) => ({ label: approveNo, value: approveNo }))}
              loading={loading}
              size="middle"
            />
          </Space>
        )}
        <div style={{}}>
          {selectedButton === "table" && (
            <Select
              value={selectedEmission}
              style={{ width: 120, visibility: selectedButton === "table" ? "visible" : "hidden" }}
              onChange={handleChangeEmission}
              options={masterDataEmission}
              loading={loading}
              size="middle"
            />
          )}
        </div>
        <div>
          <Button
            size="middle"
            style={buttonStyle("table")}
            icon={<BsTable style={{ fontSize: "18px" }} />}
            onClick={() => handleButtonMenuClick("table")}
          />
          <Button
            size="middle"
            style={buttonStyle("chart")}
            icon={<PiChartBarFill style={{ fontSize: "18px" }} />}
            onClick={() => handleButtonMenuClick("chart")}
          />
          <Button
            size="middle"
            style={buttonStyle("approve")}
            icon={<HiOutlineClipboardDocumentCheck style={{ fontSize: "18px" }} />}
            onClick={() => handleButtonMenuClick("approve")}
          />
        </div>
      </Space>
      {selectedButton === "table" && (
        <Table
          key={tableKey}
          columns={addCenterAlign(columns)}
          style={{ whiteSpace: "nowrap" }}
          dataSource={canApproveAndViewMainReport(permissions) ? (sourceData as ResponseReportVocEmission[]) : []}
          className="w-full mt-4"
          loading={loadingProfile || loadingAll || loading}
          rowKey={(record) => `${record._id}-${record.wasteWaterUnit}`}
          pagination={false}
          onChange={handleTableChange}
          summary={(pageData) => {
            if (pageData.length > 0) {
              return (
                <Table.Summary fixed="bottom">
                  {dataReportVoc?.summary &&
                    dataReportVoc.summary.length > 0 &&
                    dataReportVoc.summary.map((row, index) => {
                      //   console.log("Here row :: ", row);
                      return (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={4}>
                            <div style={{ textAlign: "center" }}>
                              <Text>{row.label}</Text>
                            </div>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} colSpan={1}>
                            <div style={{ textAlign: "start" }}>
                              <Text>{row.label === "Absolute" ? selectedUnit : row.unit}</Text>
                            </div>
                          </Table.Summary.Cell>
                          {MonthsDisplayNoTotal.map((month, idxMonth) => {
                            const keyMonth = month.dataIndex;
                            const rowValue = row[keyMonth as keyof typeof row];
                            const total =
                              typeof rowValue === "object" && rowValue !== null && UnitAllEnum.KG in rowValue && UnitAllEnum.TON in rowValue
                                ? rowValue[selectedUnit as keyof ObjectSummaryWithUnit]
                                : (rowValue as string | number);
                            return (
                              <>
                                <Table.Summary.Cell index={idxMonth + index} colSpan={1}>
                                  <div style={{ textAlign: "start" }}>
                                    <Text>
                                      {" "}
                                      {total
                                        ? row.key === "VocIntensity"
                                          ? parseAndFormatScientificNumber(`${total}`, 2)
                                          : parseAndFormatScientificNumber(`${total}`, 2)
                                        : 0}
                                    </Text>
                                  </div>
                                </Table.Summary.Cell>
                              </>
                            );
                          })}
                          <Table.Summary.Cell index={MonthsDisplayNoTotal.length} colSpan={1}>
                            <div style={{ textAlign: "start" }}>
                              <Text>
                                {row.key === "VocIntensity"
                                  ? parseAndFormatScientificNumber(`${row.total[selectedUnit as keyof ObjectSummaryWithUnit]}`, 2)
                                  : parseAndFormatScientificNumber(`${row.total[selectedUnit as keyof ObjectSummaryWithUnit]}`, 2)}
                              </Text>
                            </div>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      );
                    })}
                </Table.Summary>
              );
            }
          }}
        />
      )}

      {openModalApproveMonth && (
        <ModalApproveMonth
          open={openModalApproveMonth}
          intitailMonthly={optionMonth}
          loadingOption={loadingOptionMonth}
          loading={loadingSubmitVocReport}
          onSubmitReport={(checkedMonths) => {
            handleSubmitReport(checkedMonths);
          }}
          onCancelReport={() => {
            setOpenModalApproveMonth(false);
          }}
        />
      )}

      {selectedButton === "table" && (
        <Row className="w-full mt-4" justify={"end"}>
          {selectedButton == "table" && profile?.roles.includes(RolesNameEnum.COMPANY_ADMIN) && (
            <Button type="primary" style={{}} onClick={handleOpenModalApproveMonth} disabled={isDisableSubmitButton()}>
              Submit
            </Button>
          )}
        </Row>
      )}
      {openModalApproveStep && approveData && userId && selectedApprove && email && name && (
        <>
          {loadingOptionMonth || loadingGetApprove ? (
            <Row className="w-full" style={{ height: "4rem" }} justify={"center"}>
              <Spin style={{ display: "flex", justifyContent: "center", alignItems: "center" }} />
            </Row>
          ) : (
            <>
              <div style={{ height: "4rem" }} />
              <ModalApproveStep
                data={approveData}
                userId={userId}
                approveId={selectedApprove}
                email={email}
                name={name}
                onApprove={(approveBody, action) => {
                  handleApproveReport(approveBody, action);
                }}
              />
            </>
          )}
        </>
      )}
    </Row>
  );
};

export default ReportWWVocEmissionTable;
